import type { Vec3 } from '../../geo/types';
import { WORLD } from '../../core/config';
import type { CastleTier, HullForm, MastConfig, ShipTemplate } from './shipTemplates';

/**
 * The lines of a formed hull (docs/plans/ships.md 3): a high waist with a sunken deck, castles
 * at both ends, a raked stem and a flat raked transom. A point of the hull is named by `s`, its
 * place along the hull from the stem (0) to the stern (1), and `u`, its height from the keel (0)
 * to the rail (1); every tier samples these same lines, so a ship does not change shape when it
 * changes tier.
 */

export type FormedTemplate = ShipTemplate & { readonly form: HullForm };

/** The water in a ship's frame: its origin stands on the ground plane, the water lies below it. */
export const WATERLINE = WORLD.waterY - WORLD.groundY;

/** A castle's rail stands this far above its deck. */
export const CASTLE_RAIL = 1.0;

/* Half-breadth over keel to rail, as a fraction of the half-beam: widest at 0.65 of the height,
   drawn in to 0.84 at the rail (tumblehome). */
const SECTION: readonly (readonly [number, number])[] = [
  [0, 0],
  [0.25, 0.62],
  [0.5, 0.95],
  [0.65, 1],
  [0.85, 0.93],
  [1, 0.84],
];
const SECTION_ROWS = [0.25, 0.5, 0.65] as const;
/** Half the height of a wale, as a fraction of the height from keel to rail. */
export const WALE_HALF = 0.018;
const ROW_GAP = 0.02;
const FOREFOOT = -0.4;
const BOW_RUN = 0.25;
const STERN_RUN = 0.1;
const STEM_SPREAD = 0.2;
const TRANSOM_SPREAD = 0.15;

export function isFormed(template: ShipTemplate): template is FormedTemplate {
  return template.form !== undefined;
}

export function formedTemplate(template: ShipTemplate): FormedTemplate {
  if (!isFormed(template)) throw new Error(`${template.type} has no formed hull`);
  return template;
}

function sheer(template: ShipTemplate, s: number): number {
  const t = 2 * s - 1;
  return (t < 0 ? template.sheerFore : template.sheerAft) * t * t;
}

/** Height of the waist rail at `s`, rising towards both ends. */
export function railAt(template: FormedTemplate, s: number): number {
  return template.form.waistRail + WATERLINE + sheer(template, s);
}

/**
 * The keel runs level from a quarter of the length to nine tenths, curves up at the forefoot to the
 * stem's foot under the water, and rises along the sternpost to the transom's foot.
 */
export function keelAt(template: FormedTemplate, s: number): number {
  const deep = -template.keelDepth;
  if (s < BOW_RUN) {
    const k = (BOW_RUN - s) / BOW_RUN;
    return deep + (WATERLINE + FOREFOOT - deep) * k * k;
  }
  if (s > 1 - STERN_RUN) {
    const k = (s - (1 - STERN_RUN)) / STERN_RUN;
    return deep + (template.form.transom.tuck + WATERLINE - deep) * k;
  }
  return deep;
}

export function deckAt(template: FormedTemplate, s: number): number {
  const keel = keelAt(template, s);
  return keel + template.form.deck * (railAt(template, s) - keel);
}

function sectionBreadth(u: number): number {
  const i = Math.max(
    1,
    SECTION.findIndex(([at]) => at >= u)
  );
  const [u0, b0] = SECTION[i - 1]!;
  const [u1, b1] = SECTION[i]!;
  return b0 + ((b1 - b0) * (u - u0)) / (u1 - u0);
}

/* Towards the stem every row closes on the centre line; towards the stern the rows close on the
   transom's shield, wide at the rail and a point at the tuck. */
function endFactor(template: FormedTemplate, s: number, u: number): number {
  const t = 2 * s - 1;
  const run = Math.pow(Math.abs(t), template.hullExponent);
  if (t <= 0) return 1 - run;
  const shield = template.form.transom.width * Math.sqrt(u);
  return 1 - (1 - shield) * run;
}

function rakeShift(template: FormedTemplate, s: number, u: number): number {
  const bow = Math.max(0, 1 - s / STEM_SPREAD);
  const stern = Math.max(0, 1 - (1 - s) / TRANSOM_SPREAD);
  const { stemRake, transom } = template.form;
  return (1 - u) * (stemRake * bow * bow - transom.rake * stern * stern);
}

/** The starboard point of the hull's outer surface at `s` and height fraction `u`. */
export function hullPoint(template: FormedTemplate, s: number, u: number): Vec3 {
  const keel = keelAt(template, s);
  const y = keel + u * (railAt(template, s) - keel);
  const z = (template.beam / 2) * sectionBreadth(u) * endFactor(template, s, u);
  const x = (s - 0.5) * template.length + rakeShift(template, s, u);
  return [x, y, z];
}

export function railHalfWidthAt(template: FormedTemplate, s: number): number {
  return hullPoint(template, s, 1)[2];
}

/**
 * The height fractions of the hull's rows above the keel, the rail last: the section's own rows,
 * both edges of every wale and the deck. A section row too close to a wale or the deck gives way.
 */
export function loftRows(template: FormedTemplate): readonly number[] {
  const { wales, deck } = template.form;
  const fixed = [...wales.flatMap((w) => [w - WALE_HALF, w + WALE_HALF]), deck, 1];
  const section = SECTION_ROWS.filter((u) => fixed.every((f) => Math.abs(f - u) >= ROW_GAP));
  return [...section, ...fixed].sort((a, b) => a - b);
}

export function castleTiers(template: FormedTemplate): readonly CastleTier[] {
  return [template.form.forecastle, ...template.form.aftcastle];
}

function tiersOver(template: FormedTemplate, s: number): readonly CastleTier[] {
  return castleTiers(template).filter((tier) => s >= tier.from && s <= tier.to);
}

/** A castle tier's rail at `s` in the ship's frame, rising towards the tier's outer end. */
export function tierRailAt(template: FormedTemplate, tier: CastleTier, s: number): number {
  const span = tier.to - tier.from;
  const outward = tier === template.form.forecastle ? (tier.to - s) / span : (s - tier.from) / span;
  return tier.rail + WATERLINE + tier.sheer * Math.min(1, Math.max(0, outward));
}

export function tierDeckAt(template: FormedTemplate, tier: CastleTier, s: number): number {
  return tierRailAt(template, tier, s) - CASTLE_RAIL;
}

/** The highest rail over `s`: a castle's where one stands, else the waist rail. */
export function topAt(template: FormedTemplate, s: number): number {
  const over = tiersOver(template, s);
  if (over.length === 0) return railAt(template, s);
  return Math.max(...over.map((tier) => tierRailAt(template, tier, s)));
}

/** The highest deck over `s`: a castle's where one stands, else the waist deck. */
export function highestDeckAt(template: FormedTemplate, s: number): number {
  const over = tiersOver(template, s);
  if (over.length === 0) return deckAt(template, s);
  return Math.max(...over.map((tier) => tierDeckAt(template, tier, s)));
}

export function mastHeadAt(template: FormedTemplate, mast: MastConfig): number {
  return deckAt(template, mast.position) + mast.height;
}
